import { useRouter } from 'next/router'
import { useContext, useEffect } from 'react'

import { ConfirmChargeReturns, ConfirmPrepaymentReturns } from '@/stores/AppStore'
import cleanAdyenQueryParams from '@/utils/cleanAdyenQueryParams'
import errorMessageHandler, { isErrorOrResponseData } from '@/utils/errorMessageHandler'
import StoresContext from '@/utils/StoresContext'

interface Props {
  isRedirectable?: boolean
  handler: (redirectResult: string) => Promise<ConfirmPrepaymentReturns<'adyen'> | ConfirmChargeReturns>
}

export default function useAdyenRedirectResult({ isRedirectable, handler }: Props) {
  const router = useRouter()
  const { appStore } = useContext(StoresContext)

  useEffect(() => {
    const { redirectResult } = router.query
    if (!redirectResult || !isRedirectable) {
      return
    }

    ;(async () => {
      try {
        const { metaData } = await handler(typeof redirectResult === 'string' ? redirectResult : redirectResult[0])
        const adyenResultCode = metaData.adyenResultCode

        if (adyenResultCode === 'Authorised') {
          router.push({
            pathname: '/thank_you',
            query: cleanAdyenQueryParams(appStore.state.query)
          })
        }
      } catch (cause: unknown) {
        if (
          cause &&
          typeof cause === 'object' &&
          'response' in cause &&
          cause.response &&
          typeof cause.response === 'object' &&
          'data' in cause.response &&
          cause.response.data &&
          isErrorOrResponseData(cause.response.data)
        ) {
          const errorResponse = cause.response?.data
          appStore.state.error = errorMessageHandler(errorResponse)
        }
      }
    })()
  }, [appStore, handler, isRedirectable, router, router.query.redirectResult])
}
